import React from 'react'
import './domain-delivery-click-report.scss'
import { observer } from 'mobx-react'
import { Container } from 'typescript-ioc/es5'
import { AppState } from '../../../stores/app'
import { DomainDto } from '../../../dtos/domain'
import { DomainService } from '../../../services'
import autobind from 'autobind-decorator'
import * as moment from 'moment-timezone'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { DEFAULT_HIGHCHARTS_CONFIG } from '../../../constants'
import { HighchartsUtils } from '../../../_utils/highcharts-utils'
import { Well } from '@pushly/aqe/lib/components'

interface IProps {
    stats: any[]
    isSingleDay: boolean
    isLifetime: boolean
    timeBreakdown: string
}

interface IState {
    loading: boolean
    domain: DomainDto
}

@observer
export class DomainDeliveryClickReport extends React.Component<IProps, IState> {
    private appState: AppState
    private domainService: DomainService

    public constructor(props: IProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.domainService = Container.get(DomainService)

        this.state = {
            loading: true,
            domain: this.appState.currentDomain!,
        }
    }

    public render(): React.ReactNode {
        return (
            <Well className="domain-dc-report" title="Content Delivery" mode="ghost" hideFooter={true}>
                {this.renderHighChart()}
            </Well>
        )
    }

    @autobind
    private renderHighChart(): React.ReactNode {
        const { stats } = this.props
        if (!stats) {
            return
        }

        const xAxisData: string[] = stats.map(this.formatStatDate)
        const impressionData = stats.map((stat: any) => stat.impressions || 0)
        const clickData = stats.map((stat: any) => stat.clicks || 0)
        const ctrData = stats.map((stat: any) => parseFloat((stat.ctr || 0).toFixed(2)))
        const ctrMax = Math.max.apply(null, ctrData)

        const markerDefaults = {
            lineWidth: 2,
            fillColor: 'white',
        }

        const options = {
            ...DEFAULT_HIGHCHARTS_CONFIG,
            swContext: (): any => this.props,
            title: { text: '' },
            chart: {
                type: 'column',
                backgroundColor: 'transparent',
                height: 300,
            },
            plotOptions: {
                column: {
                    groupPadding: 0.1275,
                },
            },
            xAxis: {
                categories: xAxisData,
                title: { enabled: false },
                showLegend: false,
                tickInterval: 1,
                lineWidth: 0,
                tickWidth: 0,
                margin: 0,
                minPadding: 0.065,
                maxPadding: 0.065,
            },
            yAxis: [
                {
                    type: 'logarithmic',
                    title: { text: '' },
                    allowDecimals: false,
                    startOnTick: 0,
                    minRange: 0,
                },
                {
                    title: { text: '' },
                    min: 0,
                    max: ctrMax,
                    maxPadding: 0.2,
                    allowDecimals: false,
                    labels: {
                        format: '{value}%',
                    },
                    opposite: true,
                },
            ],
            tooltip: {
                shared: true,
                formatter: HighchartsUtils.defaultTooltipFormatter,
            },
            series: [
                {
                    className: 'imps-col',
                    type: 'column',
                    name: 'Impressions',
                    data: impressionData,
                    color: '#5d676f',
                    // color: INSIGHTS_IMPS_COLOR,
                    tooltip: {
                        pointFormatter: HighchartsUtils.commaTooltipPointFormatter,
                    },
                },
                {
                    className: 'clicks-col',
                    type: 'column',
                    name: 'Clicks',
                    data: clickData,
                    color: '#8e58da',
                    tooltip: {
                        pointFormatter: HighchartsUtils.commaTooltipPointFormatter,
                    },
                },
                {
                    className: 'ctr-line',
                    type: 'spline',
                    name: 'CTR',
                    data: ctrData,
                    color: '#c7abed',
                    yAxis: 1,
                    marker: {
                        ...markerDefaults,
                        lineColor: '#c7abed',
                    },
                    tooltip: {
                        valueSuffix: '%',
                    },
                },
            ],
        }

        return <HighchartsReact highcharts={Highcharts} options={options} />
    }

    @autobind
    private formatStatDate(stat: any): string {
        const date = moment.tz(`${stat.send_date}`, 'UTC')

        if (this.props.timeBreakdown === 'month' || this.isLifetimeBreakdown) {
            return date.format('MMM')
        }

        return date.format('MMM D')
    }

    private get isLifetimeBreakdown(): boolean {
        return this.props.isLifetime
    }
}
